import * as classNames from 'classnames';
import * as React from 'react';
import * as d3 from 'd3';
import MapAnswer from '../map-answer/map-answer';
import { DirectGeometryObject, FeatureCollection } from 'geojson';
import { geoMercator, geoPath } from 'd3-geo';
require('d3-dispatch');
require('d3-transition');

const TRANSITION_OUT_TIME = 200;
const TRANSITION_IN_TIME = 500;
const NEW_MAP_IN_TIME = 350;

interface Props {
    country: CountryAnswer;
    index: number;
    isRoundInterval: boolean;
    roundIndex: number;
    shape: Shape;
}

type Shape = FeatureCollection<DirectGeometryObject>;

export default class Map extends React.PureComponent<Props> {

    container: d3.Selection<SVGGElement, {}, null, undefined>;
    element: HTMLDivElement | null;
    height: number;
    svg: SVGElement | null;
    width: number;

    constructor(props: Props) {
        super(props);
        this.resize = this.resize.bind(this);
        this.storeElement = this.storeElement.bind(this);
        this.storeSvg = this.storeSvg.bind(this);
    }

    /**
     * Transition from one country to another.
     * @param thisShape  The original shape.
     * @param nextShape  The next shape.
     * @param isNewRound Whether this is a new round or not.
     */
    changeMap(thisShape: Shape, nextShape: Shape, isNewRound: boolean): void {

        // If the number of maps now is different than before, they will need resizing.
        if (isNewRound === true) {
            this.resizeMap();
        }

        // Generate the various stages of animating the map.
        const thisPath = this.generatePath(thisShape);
        const thisPathGone = this.generatePath(thisShape, 0);

        const nextPathGone = this.generatePath(nextShape, 0);
        const nextPath = this.generatePath(nextShape);

        if (thisPath === null || thisPathGone === null || nextPath === null || nextPathGone === null) {
            return;
        }

        // Show different animations for swapping and new rounds.
        if (isNewRound === true) {
            this.container
                .selectAll('path')
                .attr('d', nextPathGone)
                .transition()
                .duration(NEW_MAP_IN_TIME)
                .delay(this.props.index * NEW_MAP_IN_TIME)
                .ease(d3.easeCircle)
                .attr('d', nextPath);
        } else {
            this.container
                .selectAll('path')
                .transition()
                .duration(TRANSITION_OUT_TIME)
                .ease(d3.easePolyOut)
                .attr('d', thisPathGone)
                .transition()
                .duration(0)
                .attr('d', nextPathGone)
                .transition()
                .duration(TRANSITION_IN_TIME)
                .ease(d3.easeCircle)
                .attr('d', nextPath);
        }

    }

    /**
     * If the shape has loaded in time, draw it.
     */
    componentDidMount(): void {
        if (typeof this.props.shape !== 'undefined') {
            this.drawMap(this.props.shape);
        }

        // When the window resizes, the maps have to be drawn again.
        window.addEventListener('resize', this.resize);
    }

    /**
     * The component might update when it is swapped with another. Another update may be necessary if the shape is 
     * slow to load. The shape data is loaded in the background so it certain rare circumstances it might be slow
     * arriving.
     * 
     * @param nextProps Properties that may now contain a country shape.
     */
    componentWillReceiveProps(nextProps: Props): void {
        if (typeof this.props.shape === 'undefined') {
            this.drawMap(nextProps.shape);
        } else if (nextProps.country.countryCode !== this.props.country.countryCode) {
            this.changeMap(this.props.shape, nextProps.shape, this.props.roundIndex !== nextProps.roundIndex);
        }
    }

    /**
     * Remove the window resize listener.
     */
    componentWillUnmount(): void {
        window.removeEventListener('resize', this.resize);
    }

    /**
     * Draw the map for the first time.
     * @param props Properties that should contain the shape to draw.
     */
    drawMap(shape: FeatureCollection<DirectGeometryObject>): void {

        if (this.svg === null) {
            return;
        }

        // Append an SVG element that takes the entire space available.
        this.getSize();
        const svg = d3.select(this.svg)
            .attr('height', this.height)
            .attr('width', this.width);

        const initialPath = this.generatePath(shape, 0);
        const finalPath = this.generatePath(shape);

        if (initialPath === null || finalPath === null) {
            return;
        }

        // Draw or resize the map.
        if (this.container) {
            this.container.selectAll('path').attr('d', finalPath);
        } else {
            this.container = svg.append<SVGGElement>('g');
            this.container
                .append('path')
                .attr('d', initialPath)
                .transition()
                .duration(NEW_MAP_IN_TIME)
                .delay(this.props.index * NEW_MAP_IN_TIME)
                .ease(d3.easeCircle)
                .attr('d', finalPath);
        }

    }

    /**
     * Generate a shape for the country being drawn and centre the view around it.
     * @param shape Country shape being rendered.
     * @returns     SVG path.
     */
    generatePath(shape: Shape, s: number = 0.9): string | null {

        // Initial projection that will be adjusted later to centre on the country.
        const projection = geoMercator()
            .scale(1)
            .translate([0, 0]);

        const path = geoPath().projection(projection);

        // Centre the map on the country selected.
        const bounds = path.bounds(shape);
        const scale = s / Math.max((bounds[1][0] - bounds[0][0]) / this.width, (
            bounds[1][1] - bounds[0][1]) / this.height);
        const translateHorizontal = (this.width - scale * (bounds[1][0] + bounds[0][0])) / 2;
        const translateVertical = (this.height - scale * (bounds[1][1] + bounds[0][1])) / 2;

        // // Update the projection to use computed scale & translate.
        projection
            .scale(scale)
            .translate([translateHorizontal, translateVertical]);

        return path(shape);

    }

    /**
     * Figure out the size of the map. This changes in each round depending on how many maps there are.
     */
    getSize(): void {
        if (this.element === null) {
            return;
        }

        // The map should be square.
        const height = this.element.clientHeight;
        const width = this.element.clientWidth;
        if (window.innerHeight < window.innerWidth) {
            this.width = width;
            this.height = width;
        } else {
            this.height = height;
            this.width = height;
        }
    }

    /**
     * Render an SVG map.
     */
    render(): JSX.Element | null {

        const { position, initialPosition } = this.props.country;
        const change = position - initialPosition;
        const offset = change >= 0 ? `map-forwards-${change}` : `map-backwards-${Math.abs(change)}`;

        return (
            <div
                className={classNames('map', offset, { 'map-round': !this.props.isRoundInterval })}
                ref={this.storeElement}
            >
                <svg ref={this.storeSvg} />
                <strong>{this.props.country.countryName}</strong>
                <MapAnswer isRoundInterval={this.props.isRoundInterval} country={this.props.country} />
            </div>
        );

    }

    /**
     * Redraw the map when the window is resized.
     */
    resize(): void {
        this.drawMap(this.props.shape);
    }

    /**
     * Sometimes the maps will need to resize if the number shown changes between rounds.
     */
    resizeMap(): void {
        if (this.svg !== null) {

            const previousWidth = this.width;
            const previousHeight = this.height;
            this.getSize();

            if (this.width !== previousWidth || this.height !== previousHeight) {
                d3.select(this.svg)
                    .attr('height', this.height)
                    .attr('width', this.width);
            }
        }
    }

    /**
     * Store the component's HTML element for later measuring.
     * @param element The component's outer element.
     */
    storeElement(element: HTMLDivElement | null) {
        this.element = element;
    }

    /**
     * Store the SVG element that will render the map for later manipulation.
     * @param svg SVG element.
     */
    storeSvg(svg: SVGElement | null) {
        this.svg = svg;
    }

}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/map/map.tsx